import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Button } from './Button';

export interface Attachment {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
}

const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];

/**
 * Unggah berkas ke R2, ditautkan ke sepasang (entityType, entityId).
 *
 * Ukuran dan format diperiksa DI SINI juga, bukan hanya di server. Bukan demi
 * keamanan — server tetap pemutusnya — melainkan demi kecepatan: menolak
 * berkas 40 MB sebelum ia menyeberang jaringan menghemat menit-menit di
 * koneksi lapangan yang lambat.
 */
export function FileUpload({
  entityType,
  entityId,
  label = 'Lampiran',
  hint,
  readOnly = false,
  compact = false,
}: {
  entityType: string;
  entityId: string;
  label?: string;
  hint?: string;
  readOnly?: boolean;
  compact?: boolean;
}) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const queryKey = ['files', entityType, entityId];

  const { data } = useQuery({
    queryKey,
    queryFn: () =>
      api.get<Attachment[]>(
        `/files?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}`,
      ),
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData();
      body.append('file', file);
      body.append('entityType', entityType);
      body.append('entityId', entityId);
      return api.post<Attachment>('/files', body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setError(null);
      if (inputRef.current) inputRef.current.value = '';
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Gagal mengunggah berkas.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/files/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Gagal menghapus berkas.'),
  });

  function pick(file: File) {
    if (file.size > MAX_BYTES) {
      setError(`Ukuran maksimal 5 MB. Berkas Anda ${(file.size / 1024 / 1024).toFixed(1)} MB.`);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    if (!ALLOWED.includes(file.type)) {
      setError('Format harus JPG, PNG, WebP, atau PDF.');
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    upload.mutate(file);
  }

  const files = data ?? [];

  return (
    <div className={cn('flex flex-col gap-2', compact ? 'text-[13px]' : '')}>
      {!compact && <p className="text-sm font-medium text-ink">{label}</p>}

      {files.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {files.map((f) => (
            <li
              key={f.id}
              className="flex items-center gap-2 rounded-[var(--radius-input)] border border-border bg-surface px-3 py-2"
            >
              <span aria-hidden>{f.mimeType === 'application/pdf' ? '📄' : '🖼️'}</span>
              <a
                href={`/api/v1/files/${f.id}`}
                target="_blank"
                rel="noreferrer"
                className="min-w-0 flex-1 truncate text-[13px] text-primary hover:underline"
              >
                {f.fileName}
              </a>
              <span className="tabular shrink-0 text-[12px] text-ink-muted">
                {(f.sizeBytes / 1024).toFixed(0)} KB
              </span>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => remove.mutate(f.id)}
                  className="shrink-0 text-[12px] text-danger hover:underline"
                >
                  Hapus
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {!readOnly && (
        <div>
          <input
            ref={inputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.webp,.pdf"
            className="sr-only"
            id={`upload-${entityType}-${entityId}`}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) pick(file);
            }}
          />
          <Button
            type="button"
            size="sm"
            variant="secondary"
            loading={upload.isPending}
            onClick={() => inputRef.current?.click()}
          >
            ⭱ {files.length > 0 ? 'Tambah berkas' : 'Unggah berkas'}
          </Button>
        </div>
      )}

      {readOnly && files.length === 0 && (
        <p className="text-[13px] text-ink-muted">Tidak ada lampiran.</p>
      )}

      {hint && !error && <p className="text-[12px] text-ink-muted">{hint}</p>}
      {error && <p className="text-[12px] text-danger">{error}</p>}
    </div>
  );
}
